// Pagina NPS portale — valutazione intervento completato.
// Scala emoji 1-10, campo commento, submit.
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import { CheckCircle, Star, ArrowLeft, MessageSquare } from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Alert } from '../../components/ui/Alert'
import { portalApiCalls } from './portal.api'

// Emoji e label per i punteggi NPS
const NPS_SCORES = [
  { score: 1, emoji: '😡', label: 'Pessimo' },
  { score: 2, emoji: '😞', label: 'Molto scarso' },
  { score: 3, emoji: '😟', label: 'Scarso' },
  { score: 4, emoji: '😐', label: 'Insufficiente' },
  { score: 5, emoji: '😶', label: 'Sufficiente' },
  { score: 6, emoji: '🙂', label: 'Discreto' },
  { score: 7, emoji: '😊', label: 'Buono' },
  { score: 8, emoji: '😃', label: 'Molto buono' },
  { score: 9, emoji: '🤩', label: 'Ottimo' },
  { score: 10, emoji: '🏆', label: 'Eccellente' },
]

export function PortalRatingPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const reportId = searchParams.get('reportId') ?? ''
  const ticketId = searchParams.get('ticketId') ?? ''

  const [selectedScore, setSelectedScore] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const submitMutation = useMutation({
    mutationFn: () =>
      portalApiCalls.submitRating(reportId, selectedScore!, comment || undefined),
    onSuccess: () => {
      setSubmitted(true)
    },
  })

  const mutationError = submitMutation.error
    ? submitMutation.error instanceof Error
      ? submitMutation.error.message
      : 'Errore durante il salvataggio della valutazione.'
    : null

  const selectedNPS = NPS_SCORES.find((n) => n.score === selectedScore)

  const getScoreColor = (score: number): string => {
    if (score <= 3) return 'text-red-500 border-red-200 bg-red-50'
    if (score <= 6) return 'text-amber-500 border-amber-200 bg-amber-50'
    return 'text-green-600 border-green-200 bg-green-50'
  }

  const getScoreRingColor = (score: number): string => {
    if (score <= 3) return 'ring-red-400'
    if (score <= 6) return 'ring-amber-400'
    return 'ring-green-400'
  }

  if (submitted) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center py-20 text-center">
        <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-50">
          <CheckCircle className="h-12 w-12 text-[var(--fc-success)]" aria-hidden="true" />
        </div>
        <h1 className="mb-2 text-2xl font-bold text-gray-900">Grazie per il feedback!</h1>
        <p className="mb-2 text-gray-500">
          La tua valutazione è stata registrata.
        </p>
        {selectedScore !== null && (
          <div className="mb-6 flex items-center gap-2 text-2xl">
            <span aria-hidden="true">{NPS_SCORES[selectedScore - 1]?.emoji}</span>
            <span className="text-lg font-semibold text-gray-700">
              {selectedScore}/10 — {selectedNPS?.label}
            </span>
          </div>
        )}
        <p className="mb-8 max-w-sm text-sm text-gray-400">
          Il tuo feedback ci aiuta a migliorare continuamente la qualità del servizio.
        </p>
        <Button onClick={() => navigate('/portal/dashboard')}>
          Torna alla dashboard
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-xl space-y-8">
      {/* Intestazione */}
      <div>
        <button
          type="button"
          className="mb-4 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900"
          onClick={() => navigate(-1)}
          aria-label="Torna indietro"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Indietro
        </button>
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50">
            <Star className="h-7 w-7 text-amber-500" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Valuta l'intervento</h1>
          <p className="mt-2 text-gray-500">
            Come valuti il servizio ricevuto? Il tuo feedback è prezioso.
          </p>
        </div>
      </div>

      {!reportId && (
        <Alert variant="error">
          ID rapportino mancante. Accedi alla pagina di valutazione dalla lista rapportini.
        </Alert>
      )}

      {mutationError && (
        <Alert variant="error">{mutationError}</Alert>
      )}

      {/* Griglia punteggi */}
      <div
        className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
        role="group"
        aria-label="Seleziona punteggio da 1 a 10"
      >
        <p className="mb-4 text-center text-sm font-medium text-gray-700">
          Quanto sei soddisfatto del servizio?
        </p>
        <div className="grid grid-cols-5 gap-2 sm:grid-cols-10" role="radiogroup">
          {NPS_SCORES.map(({ score, emoji, label }) => (
            <button
              key={score}
              type="button"
              role="radio"
              aria-checked={selectedScore === score}
              className={`flex flex-col items-center justify-center gap-1 rounded-xl border-2 p-2 transition-all focus-visible:outline-none focus-visible:ring-2 ${
                selectedScore === score
                  ? `${getScoreColor(score)} ${getScoreRingColor(score)} ring-2`
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
              onClick={() => setSelectedScore(score)}
              title={`${score} — ${label}`}
            >
              <span className="text-2xl" aria-hidden="true">{emoji}</span>
              <span
                className={`text-xs font-semibold ${
                  selectedScore === score ? '' : 'text-gray-500'
                }`}
              >
                {score}
              </span>
            </button>
          ))}
        </div>

        {/* Label punteggio selezionato */}
        {selectedNPS && (
          <p className="mt-4 text-center text-sm font-medium text-gray-700">
            <span className="mr-1" aria-hidden="true">{selectedNPS.emoji}</span>
            {selectedNPS.score}/10 — {selectedNPS.label}
          </p>
        )}

        {/* Barra scala */}
        <div
          className="mt-4 flex justify-between text-xs text-gray-400"
          aria-hidden="true"
        >
          <span>Non soddisfatto</span>
          <span>Molto soddisfatto</span>
        </div>
      </div>

      {/* Commento */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <label
          htmlFor="rating-comment"
          className="flex items-center gap-2 text-sm font-medium text-gray-900"
        >
          <MessageSquare className="h-4 w-4 text-gray-400" aria-hidden="true" />
          Commento
          <span className="font-normal text-gray-400">(opzionale)</span>
        </label>
        <textarea
          id="rating-comment"
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Hai suggerimenti per migliorare il servizio? Raccontaci la tua esperienza..."
          maxLength={2000}
          className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[var(--fc-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--fc-primary)]"
          aria-label="Commento opzionale sulla valutazione"
        />
        <p className="mt-1 text-right text-xs text-gray-400">
          {comment.length}/2000 caratteri
        </p>
      </div>

      {/* Submit */}
      <div className="flex justify-center">
        <Button
          size="lg"
          onClick={() => submitMutation.mutate()}
          disabled={!selectedScore || !reportId}
          isLoading={submitMutation.isPending}
          className="px-10"
          leftIcon={<Star className="h-4 w-4" />}
        >
          Invia valutazione
        </Button>
      </div>

      {!selectedScore && (
        <p className="text-center text-xs text-gray-400">
          Seleziona un punteggio per procedere
        </p>
      )}
    </div>
  )
}
